import { constVoid, DOM, flow, IOO, n, O, pipe, RNEA, RR, s, v } from '@packages/prelude'
import { Swiper } from 'swiper'
import { SwiperOptions } from 'swiper/types'
import { Pagination, Thumbs, Mousewheel, FreeMode, HashNavigation, Scrollbar } from 'swiper/modules'

import '../css/main.scss'
import 'bootstrap'

const calcContentSize = () => pipe(
  IOO.of({ document }),
  IOO.bind('contents', ({ document }) => pipe(document, DOM.querySelectorAll('.content:not(.no-overflow)')) as IOO.IOOption<RNEA.ReadonlyNonEmptyArray<HTMLElement>>),
  IOO.map(({ contents }) => {
    const height = document.documentElement.clientHeight

    pipe(
      contents,
      RNEA.map(block => {
        const multiplier = Math.ceil(block.scrollHeight / height)

        if (multiplier > 1) {
          pipe(
            block.parentElement,
            O.fromNullable,
            O.foldMap(v.Monoid)(
              section => {
                const hasBorderLeft = section.classList.contains('section-border-left')
                const hasBorderRight = section.classList.contains('section-border-right')

                const sectionWidth = section.clientWidth

                const columnWidth = (hasBorderLeft && !hasBorderRight || !hasBorderLeft && hasBorderRight) ? (sectionWidth - 4) : hasBorderLeft && hasBorderRight ? (sectionWidth - 8) : sectionWidth

                section.style.width = `${sectionWidth * multiplier}px`
                block.style.columnWidth = `${columnWidth}px`
                block.classList.add('columns')
                block.classList.add(`columns-${multiplier}`)
              }
            )
          )
        }
      })
    )
  })
)

const showHint = (swiper: Swiper) => pipe(
  IOO.of({ document }),
  IOO.bind('scrollHint', ({ document }) => pipe(document, DOM.querySelector('.scroll-hint')) as IOO.IOOption<HTMLElement>),
  IOO.map(({ scrollHint }) => {
    scrollHint.classList.toggle('show', swiper.isBeginning)

    scrollHint.addEventListener('click', () => {
      swiper.translateTo(-200, 300)
      scrollHint.classList.remove('show')
    }, { once: true })
  })
)

const showHintMobile = (swiper: Swiper) => pipe(
  IOO.of({ document }),
  IOO.bind('scrollHint', ({ document }) => pipe(document, DOM.querySelector('.scroll-hint')) as IOO.IOOption<HTMLElement>),
  IOO.map(({ scrollHint }) => {
    scrollHint.classList.toggle('show-mobile', swiper.isBeginning)

    scrollHint.addEventListener('click', () => {
      swiper.translateTo(-200, 300)
      scrollHint.classList.remove('show-mobile')
    }, { once: true })
  })
)

const isTrackPad = (e: WheelEvent) => Object.is(e.deltaY, -0) || !Object.is(e.deltaX, -0)

const makeSwiper = (selector: string, options: SwiperOptions) =>
  pipe(
    IOO.of({ document }),
    IOO.chain(({ document }) => pipe(document, DOM.querySelector(`#${selector}`)) as IOO.IOOption<HTMLElement>),
    IOO.fold(
      () => constVoid,
      element => IOO.of(new Swiper(element, options))
    )
  )()

const Swipers: Record<string, SwiperOptions> = {
  sectionSlider: {
    modules: [Thumbs, Pagination, Mousewheel, FreeMode, HashNavigation, Scrollbar],
    direction: 'horizontal',
    slidesPerView: 'auto',
    on: {
      slideChange: (swiper => {
        pipe(
          IOO.of({ window }),
          IOO.chain(() => IOO.of(swiper.slides[swiper.activeIndex]?.dataset.hash)),
          IOO.map(flow(
              O.fromNullable,
              O.foldMap(v.Monoid)(hash => window.location.hash = hash)
            )
          )
        )()

        showHint(swiper)()
        showHintMobile(swiper)()

        pipe(
          IOO.of({ document }),
          IOO.bind('navbarToggle', ({ document }) => pipe(document, DOM.querySelector('.navbar-toggle')) as IOO.IOOption<HTMLButtonElement>),
          IOO.bind('navbar', ({ document }) => pipe(document, DOM.querySelector('#collapseNavbar'))),
          IOO.map(({ navbarToggle, navbar }) => {
            if (navbar.classList.contains('show')) navbarToggle.click()
          })
        )()
      }),
      init: swiper => {
        calcContentSize()
        setTimeout(() => showHintMobile(swiper)(), 3000)
        setTimeout(() => showHint(swiper)(), 10000)
      },
      afterInit: swiper => {
        pipe(
          IOO.of({ window }),
          IOO.chain(({ window }) => IOO.of(window.location.hash)),
          IOO.fold(
            () => constVoid,
            hash => pipe(
              IOO.of({ document }),
              IOO.bind('parent', ({ document }) => pipe(document, DOM.querySelector(`#sectionSlider > .swiper-wrapper > .swiper-slide:has( .headline-block${hash})`)) as IOO.IOOption<HTMLElement>),
              IOO.bind('nested', ({ document }) => pipe(document, DOM.querySelector(`#sectionInnerSlider .swiper-slide:has( .headline-block${hash})`)) as IOO.IOOption<HTMLElement>),
              IOO.map(({ parent, nested }) => {
                if (window.location.hash && parent.dataset.hash && nested) {
                  window.location.hash = parent.dataset.hash

                  const totalOffsetLeft = parent.offsetLeft * -1
                  const offsetLeft = nested.offsetLeft

                  setTimeout(() => swiper.setTranslate(totalOffsetLeft - offsetLeft), 100)
                }
              })
            )
          )
        )()
      },
      scroll: (swiper, event) => {
        swiper.wrapperEl.classList.toggle('scrolling', !isTrackPad(event))
        showHint(swiper)()
        showHintMobile(swiper)()
      },
      click: swiper => setTimeout(() => swiper.update(), 500)
    },
    thumbs: {
      swiper: new Swiper('#slideNavbar', {
        slidesPerView: 'auto',
        slideToClickedSlide: true
      })
    },
    hashNavigation: {
      watchState: true
    },
    freeMode: {
      enabled: true,
      sticky: false
    },
    observeParents: true,
    observeSlideChildren: true,
    observer: true,
    resizeObserver: true,
    breakpoints: {
      768: {
        mousewheel: {
          enabled: true,
          sensitivity: 2
        },
        scrollbar: {
          el: '.swiper-scrollbar',
          hide: true,
          draggable: true,
          snapOnRelease: false
        }
      }
    }
  },
  sectionInnerSlider: {
    modules: [FreeMode],
    direction: 'horizontal',
    slidesPerView: 'auto',
    on: {
      init: calcContentSize(),
      resize: calcContentSize(),
      click: swiper => setTimeout(() => swiper.update(), 400)
    },
    freeMode: {
      enabled: true,
      sticky: false
    },
    observeParents: true,
    observeSlideChildren: true,
    observer: true,
    resizeObserver: true
  }
}

pipe(
  Swipers,
  RR.foldMapWithIndex(s.Ord)(v.Monoid)((selector, options) => makeSwiper(selector, options))
)

pipe(
  IOO.of(({ document })),
  IOO.bind('imageBlocks', ({ document }) => pipe(document, DOM.querySelectorAll('.image-hover-block')) as IOO.IOOption<RNEA.ReadonlyNonEmptyArray<HTMLElement>>),
  IOO.map(({ imageBlocks }) =>
    pipe(
      imageBlocks,
      RNEA.map(imageBlock => {
        DOM.addEventListener('click')(() => () => {
          imageBlock.classList.toggle('open')
        })(imageBlock)()
      })
    )
  )
)()

pipe(
  IOO.of(({ document })),
  IOO.bind('videoBlocks', ({ document }) => pipe(document, DOM.querySelectorAll('.video-hover-block')) as IOO.IOOption<RNEA.ReadonlyNonEmptyArray<HTMLElement>>),
  IOO.map(({ videoBlocks }) =>
    pipe(
      videoBlocks,
      RNEA.map(videoBlock => {
        DOM.addEventListener('click')(() => () => {
          videoBlock.classList.toggle('open')
        })(videoBlock)()
      })
    )
  )
)()
